/**
 * Extracted Elements List Component
 * Displays the list of extracted elements with details
 */

import React, { useState } from 'react';
import { useViewerStore } from '@/store/viewerStore';
import { ViewButton } from '@/components/common/ViewButton';
import { ViewerService } from '@/services/core/viewerTools';
import type { ExtractedElement } from '@/store/extractionStore';

interface ExtractedElementsListProps {
  elements: ExtractedElement[];
}

export function ExtractedElementsList({ elements }: ExtractedElementsListProps) {
  const { viewer } = useViewerStore();
  const [expandedElements, setExpandedElements] = useState<Set<number>>(new Set());
  const [viewedElement, setViewedElement] = useState<number | null>(null);

  const toggleExpand = (dbId: number) => {
    const newExpanded = new Set(expandedElements);
    if (newExpanded.has(dbId)) {
      newExpanded.delete(dbId);
    } else {
      newExpanded.add(dbId);
    }
    setExpandedElements(newExpanded);
  };

  const handleViewElement = (dbId: number) => {
    setViewedElement(dbId);
  };

  const handleShowAll = () => {
    if (viewer) {
      const allDbIds = elements.map(el => el.dbId);
      ViewerService.highlightElements(viewer, allDbIds);
    }
    setViewedElement(null);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold flex items-center gap-2 text-white">
          Extracted Elements
          <span className="text-sm font-normal text-white">
            ({elements.length} items)
          </span>
        </h3>
        {viewedElement && (
          <button
            className="btn btn-secondary btn-small"
            onClick={handleShowAll}
          >
            Show All
          </button>
        )}
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto">
        {elements.map((element) => {
          const isExpanded = expandedElements.has(element.dbId);
          const isViewed = viewedElement === element.dbId;

          return (
            <div
              key={element.dbId}
              className={`border rounded-lg transition-all ${
                isViewed 
                  ? 'border-blue-500 bg-blue-500/10' 
                  : 'border-gray-700 bg-dark-200 hover:border-gray-600'
              }`}
            >
              <div className="p-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <button
                        className="text-white hover:text-white"
                        onClick={() => toggleExpand(element.dbId)}
                      >
                        {isExpanded ? '▼' : '▶'}
                      </button>
                      <span className="font-medium text-white">{element.name}</span>
                      {isViewed && (
                        <span className="text-xs bg-blue-500/20 text-white px-2 py-0.5 rounded">
                          Viewing
                        </span>
                      )}
                    </div>
                    
                    <div className="ml-6 mt-1 text-sm text-white">
                      {element.family && (
                        <span className="mr-3">
                          <span className="text-white">Family:</span> {element.family}
                        </span>
                      )}
                      {element.type && (
                        <span className="mr-3">
                          <span className="text-white">Type:</span> {element.type}
                        </span>
                      )}
                      {element.level && (
                        <span>
                          <span className="text-white">Level:</span> {element.level}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <ViewButton
                      dbId={element.dbId}
                      onView={() => handleViewElement(element.dbId)}
                    />
                    <span className="text-xs text-white">
                      ID: {element.dbId}
                    </span>
                  </div>
                </div>

                {isExpanded && Object.keys(element.properties).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-700">
                    <div className="ml-6 space-y-1">
                      <div className="text-xs font-semibold text-white mb-2">
                        Properties:
                      </div>
                      {Object.entries(element.properties).map(([key, value]) => (
                        <div key={key} className="text-xs flex text-white">
                          <span className="text-white min-w-[120px]">{key}:</span>
                          <span className="text-white">{value || 'N/A'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}